import { useState } from "react";
import { useTheme } from "@/hooks/useTheme";

const cases = [
  {
    industry: "Home Services",
    tag: "Field Services",
    image: "https://public.readdy.ai/ai/img_res/69733aa1de8ccacf87722dcfe0adbf5c.jpg",
    problem: "Missed calls during peak hours leading to lost bookings",
    solution: "AI call handling + automated scheduling",
    result: "35% increase in booked jobs within 30 days",
    metric: "+35%",
    metricLabel: "Booked Jobs",
  },
  {
    industry: "Healthcare Provider",
    tag: "Healthcare",
    image: "https://readdy.ai/api/search-image?query=modern%20healthcare%20clinic%20reception%20desk%20clean%20white%20interior%20professional%20medical%20staff%20patient%20intake%20digital%20tablet%20minimal%20bright%20environment&width=800&height=500&seq=case002&orientation=landscape",
    problem: "Manual intake and delayed patient responses",
    solution: "Integrated intake system with automated follow-ups",
    result: "Faster patient onboarding and improved retention",
    metric: "2×",
    metricLabel: "Faster Onboarding",
  },
  {
    industry: "Restaurant Group",
    tag: "Hospitality",
    image: "https://readdy.ai/api/search-image?query=upscale%20restaurant%20dining%20room%20elegant%20table%20settings%20warm%20ambient%20lighting%20modern%20interior%20design%20minimal%20clean%20aesthetic%20guests%20dining&width=800&height=500&seq=case003&orientation=landscape",
    problem: "No centralized reservation and follow-up system",
    solution: "Automated booking + guest re-engagement workflows",
    result: "Increased repeat visits and reduced no-shows",
    metric: "40%",
    metricLabel: "Fewer No-Shows",
  },
  {
    industry: "Legal Firm",
    tag: "Legal",
    image: "https://readdy.ai/api/search-image?query=modern%20law%20office%20professional%20clean%20desk%20legal%20books%20minimal%20interior%20design%20bright%20natural%20light%20sophisticated%20workspace%20attorney&width=800&height=500&seq=case004&orientation=landscape",
    problem: "Slow lead response and missed consultations",
    solution: "Instant intake automation and qualification system",
    result: "Higher consultation conversion rate",
    metric: "3×",
    metricLabel: "Conversion Rate",
  },
  {
    industry: "Financial Advisory",
    tag: "Finance",
    image: "https://readdy.ai/api/search-image?query=financial%20advisory%20office%20modern%20clean%20professional%20workspace%20dual%20monitors%20charts%20data%20visualization%20minimal%20bright%20corporate%20interior&width=800&height=500&seq=case005&orientation=landscape",
    problem: "Disconnected CRM and client communication",
    solution: "Unified system with lifecycle automation",
    result: "Improved client engagement and pipeline visibility",
    metric: "60%",
    metricLabel: "Pipeline Visibility",
  },
];

export default function CaseStudiesSection() {
  const [active, setActive] = useState(0);
  const { isDark } = useTheme();

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const bg = isDark ? "#1C1A22" : "#FBF6EE";
  const headlineColor = isDark ? "white" : "#1C1A22";
  const mutedColor = isDark ? "rgba(255,255,255,0.38)" : "rgba(28,26,34,0.45)";
  const cardBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(28,26,34,0.1)";
  const activeTabBg = isDark ? "rgba(232,118,26,0.08)" : "rgba(232,118,26,0.07)";
  const activeTabBorder = isDark ? "rgba(232,118,26,0.3)" : "rgba(232,118,26,0.4)";
  const inactiveTabBorder = isDark ? "rgba(255,255,255,0.05)" : "rgba(28,26,34,0.08)";
  const detailsBg = isDark ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.5)";
  const gradientFrom = isDark ? "#1C1A22" : "#FBF6EE";
  const badgeBg = isDark ? "rgba(28,26,34,0.8)" : "rgba(251,246,238,0.85)";
  const dividerGradient = isDark
    ? "linear-gradient(90deg, transparent, rgba(245,200,66,0.25), rgba(232,118,26,0.2), transparent)"
    : "linear-gradient(90deg, transparent, rgba(245,200,66,0.35), rgba(232,118,26,0.3), transparent)";

  return (
    <section id="case-studies" className="py-28 md:py-36 transition-colors duration-500" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <div className="h-px w-full" style={{ background: dividerGradient }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.25em] font-medium mb-5" style={{ color: "#F5C842" }}>Proof</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-6xl font-thin leading-tight tracking-tight transition-colors duration-500" style={{ color: headlineColor }}>
              Real Systems.
              <br />
              <span className="font-semibold">Real Results.</span>
            </h2>
            <p className="text-sm md:text-base max-w-xs leading-relaxed font-light transition-colors duration-500" style={{ color: mutedColor }}>
              When your systems work together, your business performs better.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Left tabs */}
          <div className="lg:col-span-4 flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {cases.map((c, i) => (
              <button
                key={c.industry}
                onClick={() => setActive(i)}
                className="flex-shrink-0 lg:flex-shrink text-left px-5 py-4 rounded-xl transition-all duration-300 cursor-pointer"
                style={{
                  background: active === i ? activeTabBg : "transparent",
                  border: active === i ? `1px solid ${activeTabBorder}` : `1px solid ${inactiveTabBorder}`,
                }}
              >
                <div
                  className="text-[10px] uppercase tracking-widest mb-1.5 font-medium"
                  style={{ color: active === i ? "#E8761A" : mutedColor }}
                >
                  {c.tag}
                </div>
                <div className="text-sm font-medium leading-snug transition-colors duration-300" style={{ color: active === i ? headlineColor : mutedColor }}>
                  {c.industry}
                </div>
                <div
                  className="text-xl font-semibold mt-1"
                  style={active === i ? {
                    background: "linear-gradient(135deg, #E8761A, #F5C842)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  } : { color: isDark ? "rgba(255,255,255,0.15)" : "rgba(28,26,34,0.15)" }}
                >
                  {c.metric}
                  <span
                    className="text-xs font-normal ml-1.5"
                    style={{ color: active === i ? "rgba(232,118,26,0.6)" : isDark ? "rgba(255,255,255,0.1)" : "rgba(28,26,34,0.15)", WebkitTextFillColor: "unset" }}
                  >
                    {c.metricLabel}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Right content */}
          <div
            className="lg:col-span-8 rounded-2xl overflow-hidden transition-all duration-500"
            style={{ border: `1px solid ${cardBorder}` }}
          >
            <div className="relative h-64 md:h-80">
              <img
                key={active}
                src={cases[active].image}
                alt={cases[active].industry}
                className="w-full h-full object-cover object-top"
                style={{ animation: "fadeIn 0.4s ease" }}
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(to top, ${gradientFrom} 0%, ${gradientFrom}4D 50%, transparent 100%)` }}
              />
              <div
                className="absolute top-5 right-5 px-4 py-2.5 rounded-xl backdrop-blur-md"
                style={{ background: badgeBg, border: "1px solid rgba(232,118,26,0.25)" }}
              >
                <div
                  className="font-bold text-2xl leading-none"
                  style={{
                    background: "linear-gradient(135deg, #E8761A, #F5C842)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {cases[active].metric}
                </div>
                <div className="text-[10px] uppercase tracking-wider mt-0.5" style={{ color: mutedColor }}>{cases[active].metricLabel}</div>
              </div>
              <div className="absolute bottom-5 left-6">
                <span
                  className="text-[10px] uppercase tracking-widest font-medium px-3 py-1 rounded-full"
                  style={{ background: "rgba(232,118,26,0.15)", color: "#E8761A", border: "1px solid rgba(232,118,26,0.25)" }}
                >
                  {cases[active].tag}
                </span>
              </div>
            </div>

            <div
              className="p-7 md:p-9 grid grid-cols-1 md:grid-cols-3 gap-6 transition-colors duration-500"
              style={{ background: detailsBg }}
            >
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400/70"></div>
                  <span className="text-[10px] uppercase tracking-widest font-medium" style={{ color: mutedColor }}>Problem</span>
                </div>
                <p className="text-sm leading-relaxed font-light" style={{ color: isDark ? "rgba(255,255,255,0.5)" : "rgba(28,26,34,0.55)" }}>{cases[active].problem}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#5A7BA8" }}></div>
                  <span className="text-[10px] uppercase tracking-widest font-medium" style={{ color: mutedColor }}>Solution</span>
                </div>
                <p className="text-sm leading-relaxed font-light transition-colors duration-500" style={{ color: headlineColor }}>{cases[active].solution}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: "#F5C842" }}></div>
                  <span className="text-[10px] uppercase tracking-widest font-medium" style={{ color: mutedColor }}>Result</span>
                </div>
                <p className="text-sm leading-relaxed font-medium" style={{ color: isDark ? "#F5C842" : "#c49a10" }}>{cases[active].result}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <button
            onClick={() => scrollTo("book-audit")}
            className="px-8 py-3.5 rounded-full text-white font-semibold text-[14px] transition-all duration-200 cursor-pointer whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #E8761A, #F5C842)" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLElement).style.opacity = "0.9")}
            onMouseLeave={(e) => ((e.currentTarget as HTMLElement).style.opacity = "1")}
          >
            See What a Connected System Can Do
          </button>
        </div>
      </div>

      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </section>
  );
}
